import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigateWithParams } from '../hooks/useNavigateWithParams';
import { motion } from 'framer-motion';
import {
  Rocket,
  Sparkles,
  Link2,
  CalendarDays,
  User,
  ClipboardCheck,
  Check,
  Circle,
  Star,
  AlertTriangle,
  ArrowLeft,
  Save,
  Lock,
  Loader2,
  FileQuestion,
  Home,
  ExternalLink,
  ChevronDown,
  Image as ImageIcon,
  Activity,
  Video,
} from 'lucide-react';
import { useTenant } from '../context/TenantContext';
import {
  getLaunchConfig,
  saveLaunchConfig,
  calculateEmailDates,
  LANDING_PAGE_THEMES,
  TIME_OPTIONS,
  type LaunchConfig,
} from '../lib/launchPlanner';
import { getProfile, upsertProfile, type AdvisorProfile } from '../lib/advisorProfile';

// ─── Types ───────────────────────────────────────────────────────────────────

interface FormState {
  title: string;
  subtitle: string;
  registration_link: string;
  webinar_date: string;
  webinar_time: string;
  landing_page_theme: string;
  includePresenterImage: boolean;
  presenter_image: string;
  pipeline_id: string;
  pipeline_stage_id: string;
  zoom_join_link: string;
}

interface ProfileForm {
  advisor_name: string;
  company_name: string;
  bio: string;
  advisor_headshot: string;
  cta_link: string;
}

import { formatDate, parseLocalDate, isPast } from '../lib/dateUtils';

import { ImagePreview } from '../components/ui/ImagePreview';

// ─── Card Wrapper ────────────────────────────────────────────────────────────

function LaunchCard({
  index,
  icon: Icon,
  title,
  accentBorder,
  warningBorder,
  children,
}: {
  index: number;
  icon: React.ElementType;
  title: string;
  accentBorder?: boolean;
  warningBorder?: boolean;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 + index * 0.1, duration: 0.5 }}
      className={`relative group bg-slate-900/50 backdrop-blur-xl rounded-2xl p-6 transition-all duration-300
        ${warningBorder ? 'border border-amber-500/40' : 'border border-slate-700/50'}
        ${accentBorder ? 'border-l-4 border-l-primary-500' : ''}
        hover:border-slate-600/60 hover:shadow-lg hover:shadow-primary-500/5`}
    >
      {/* Top accent line */}
      <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-primary-500/30 to-transparent" />

      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center">
          <Icon className="w-4 h-4 text-primary-400" />
        </div>
        <h2 className="text-lg font-semibold text-slate-100">{title}</h2>
      </div>

      {children}
    </motion.div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function LaunchPlanner() {
  const navigate = useNavigateWithParams();
  const { id: webinarId } = useParams<{ id: string }>();
  const { locationId } = useTenant();

  // Page state
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Form state
  const [form, setForm] = useState<FormState>({
    title: '',
    subtitle: '',
    registration_link: '',
    webinar_date: '',
    webinar_time: '',
    landing_page_theme: 'professional-authority',
    includePresenterImage: false,
    presenter_image: '',
    pipeline_id: '',
    pipeline_stage_id: '',
    zoom_join_link: '',
  });
  const [savedForm, setSavedForm] = useState<FormState | null>(null);

  // Profile state
  const [profile, setProfile] = useState<AdvisorProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [profileForm, setProfileForm] = useState<ProfileForm>({
    advisor_name: '',
    company_name: '',
    bio: '',
    advisor_headshot: '',
    cta_link: '',
  });
  const [profileSaving, setProfileSaving] = useState(false);

  const [pipelines, setPipelines] = useState<Array<{
    id: string;
    name: string;
    stages: Array<{ id: string; name: string }>;
  }>>([]);
  const [pipelinesLoading, setPipelinesLoading] = useState(false);

  const isDirty = savedForm !== null && JSON.stringify(form) !== JSON.stringify(savedForm);
  const profileComplete = !!(profile && profile.advisor_name);

  // ─── Data loading ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!webinarId) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    async function load() {
      try {
        const config = await getLaunchConfig(webinarId!);
        if (!config) {
          setNotFound(true);
          return;
        }

        const initial: FormState = {
          title: config.title,
          subtitle: config.subtitle,
          registration_link: config.registration_link,
          webinar_date: config.webinar_date,
          webinar_time: config.webinar_time,
          landing_page_theme: config.landing_page_theme || 'professional-authority',
          includePresenterImage: !!config.presenter_image,
          presenter_image: config.presenter_image,
          pipeline_id: config.pipeline_id,
          pipeline_stage_id: config.pipeline_stage_id,
          zoom_join_link: config.zoom_join_link,
        };
        setForm(initial);
        setSavedForm(initial);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [webinarId]);

  useEffect(() => {
    async function loadProfile() {
      try {
        const p = await getProfile(locationId);
        setProfile(p);
      } catch {
        // ignore
      } finally {
        setProfileLoading(false);
      }
    }
    loadProfile();
  }, [locationId]);

  useEffect(() => {
    async function fetchPipelines() {
      setPipelinesLoading(true);
      try {
        const res = await fetch(`/api/ghl/pipelines?location_id=${locationId}`);
        if (res.ok) {
          const data = await res.json();
          setPipelines(data.pipelines || []);
        }
      } catch {
        // Non-critical
      } finally {
        setPipelinesLoading(false);
      }
    }
    if (locationId) fetchPipelines();
  }, [locationId]);

  // ─── Unsaved changes warning ───────────────────────────────────────────────

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ─── Toast ─────────────────────────────────────────────────────────────────

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ─── Form update ───────────────────────────────────────────────────────────

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // ─── Save draft ────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!webinarId) return;
    setSaving(true);
    try {
      const payload: Partial<LaunchConfig> = {
        title: form.title,
        subtitle: form.subtitle,
        registration_link: form.registration_link,
        webinar_date: form.webinar_date,
        webinar_time: form.webinar_time,
        landing_page_theme: form.landing_page_theme,
        presenter_image: form.includePresenterImage ? form.presenter_image : '',
        pipeline_id: form.pipeline_id,
        pipeline_stage_id: form.pipeline_stage_id,
        zoom_join_link: form.zoom_join_link,
        launch_status: 'draft',
      };
      await saveLaunchConfig(webinarId, payload);
      setSavedForm({ ...form });
      showToast('success', 'Draft saved successfully');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ─── Profile save ─────────────────────────────────────────────────────────

  const handleProfileSave = async () => {
    setProfileSaving(true);
    try {
      await upsertProfile({
        location_id: locationId,
        ...profileForm,
      } as Partial<AdvisorProfile> & { location_id: string });
      const refreshed = await getProfile(locationId);
      setProfile(refreshed);
      setShowProfileForm(false);
      showToast('success', 'Profile saved');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setProfileSaving(false);
    }
  };

  // ─── Email timeline ────────────────────────────────────────────────────────

  const emailTimeline = useMemo(() => {
    if (!form.webinar_date) return null;
    const d = parseLocalDate(form.webinar_date);
    if (isNaN(d.getTime())) return null;
    return calculateEmailDates(d);
  }, [form.webinar_date]);

  // ─── Readiness checklist ───────────────────────────────────────────────────

  const checklist = useMemo(() => {
    const items = [
      { label: 'Webinar Script', done: true },
      { label: 'Marketing Emails', done: true },
      { label: 'Follow-Up Emails', done: true },
      { label: 'Social Media Posts', done: true },
      { label: 'Landing Page Content', done: true },
      { label: 'Registration Link', done: !!form.registration_link.trim() },
      { label: 'Webinar Date', done: !!form.webinar_date },
      { label: 'Zoom Link', done: !!form.zoom_join_link.trim() },
      { label: 'Pipeline Selected', done: !!form.pipeline_id },
      { label: 'Profile Complete', done: profileComplete },
    ];
    const doneCount = items.filter((i) => i.done).length;
    return { items, doneCount, total: items.length };
  }, [form.registration_link, form.webinar_date, form.zoom_join_link, form.pipeline_id, profileComplete]);

  // ─── Not found state ──────────────────────────────────────────────────────

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-6">
              <FileQuestion className="w-8 h-8 text-slate-500" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Webinar Not Found</h2>
            <p className="text-slate-400 mb-6">
              We couldn't find this webinar. It may have been deleted or the link is invalid.
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-500 text-white font-medium rounded-lg transition-colors"
            >
              <Home className="w-4 h-4" />
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Loading state ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
          <p className="text-slate-400">Loading launch configuration...</p>
        </div>
      </div>
    );
  }

  // ─── Timeline node ─────────────────────────────────────────────────────────

  const TimelineNode = ({
    date,
    label,
    variant,
    isCenter,
  }: {
    date: Date;
    label: string;
    variant: 'blue' | 'purple';
    isCenter?: boolean;
  }) => {
    const past = isPast(date);
    return (
      <div className="relative flex items-start gap-4 pb-5 last:pb-0">
        {/* Dot */}
        <div className="relative z-10 flex-shrink-0 mt-0.5">
          {isCenter ? (
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-500 to-purple-500 flex items-center justify-center shadow-lg shadow-primary-500/30">
              <Star className="w-3.5 h-3.5 text-white" />
            </div>
          ) : (
            <div
              className={`w-3 h-3 rounded-full mt-1.5 ml-2 ${
                variant === 'blue'
                  ? 'bg-primary-500 shadow-sm shadow-primary-500/50'
                  : 'bg-purple-500 shadow-sm shadow-purple-500/50'
              }`}
            />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 flex items-baseline gap-3">
          <span
            className={`text-sm font-medium ${
              isCenter
                ? 'text-white text-base'
                : variant === 'blue'
                ? 'text-primary-300'
                : 'text-purple-300'
            }`}
          >
            {label}
          </span>
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${
              isCenter
                ? 'bg-primary-500/20 text-primary-300 font-medium'
                : 'bg-slate-800 text-slate-400'
            }`}
          >
            {formatDate(date)}
          </span>
          {past && !isCenter && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
              Past date
            </span>
          )}
        </div>
      </div>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen p-4 md:p-8 pb-32">
      <div className="w-full max-w-3xl mx-auto space-y-6">
        {/* ── Page Header ────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-3 pt-4"
        >
          {/* Subtle pulsing glow */}
          <div className="relative inline-block">
            <div className="absolute inset-0 rounded-full bg-primary-500/20 blur-2xl animate-pulse" />
            <div className="relative inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20">
              <Rocket className="w-4 h-4 text-primary-400" />
              <span className="text-sm text-primary-300 font-medium">Mission Control</span>
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary-400 via-primary-300 to-purple-400 bg-clip-text text-transparent">
            Prepare Your Launch
          </h1>
          <p className="text-slate-400 text-base">
            Configure your webinar deployment
          </p>
        </motion.div>

        {/* ── Card 1: Webinar Details ────────────────────────────────────── */}
        <LaunchCard index={0} icon={Sparkles} title="Webinar Details">
          <div className="space-y-5">
            {/* Title */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-300">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="Your webinar title"
                className="w-full px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary-500/50 transition-colors"
              />
            </div>

            {/* Subtitle */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-300">Subtitle</label>
              <input
                type="text"
                value={form.subtitle}
                onChange={(e) => updateField('subtitle', e.target.value)}
                placeholder="Your webinar subtitle"
                className="w-full px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary-500/50 transition-colors"
              />
            </div>

            {/* Landing Page Theme */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-300">Landing Page Theme</label>
              <div className="relative">
                <select
                  value={form.landing_page_theme}
                  onChange={(e) => updateField('landing_page_theme', e.target.value)}
                  className="w-full appearance-none px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 focus:outline-none focus:border-primary-500/50 transition-colors pr-10"
                >
                  {LANDING_PAGE_THEMES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
              </div>
            </div>

            {/* Include Presenter Image toggle */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-slate-500" />
                  Include Presenter Image
                </label>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.includePresenterImage}
                  onClick={() =>
                    updateField('includePresenterImage', !form.includePresenterImage)
                  }
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    form.includePresenterImage
                      ? 'bg-primary-500'
                      : 'bg-slate-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      form.includePresenterImage ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {form.includePresenterImage && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-start gap-4"
                >
                  <div className="flex-1">
                    <input
                      type="text"
                      value={form.presenter_image}
                      onChange={(e) => updateField('presenter_image', e.target.value)}
                      placeholder="https://example.com/your-photo.jpg"
                      className="w-full px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                    />
                  </div>
                  <ImagePreview url={form.presenter_image} size={48} />
                </motion.div>
              )}
            </div>
          </div>
        </LaunchCard>

        {/* ── Card 2: Registration ───────────────────────────────────────── */}
        <LaunchCard index={1} icon={Link2} title="Registration" accentBorder>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-slate-300">
              Registration Link
              <span className="text-red-400 ml-1">*</span>
            </label>
            <input
              type="url"
              value={form.registration_link}
              onChange={(e) => updateField('registration_link', e.target.value)}
              placeholder="Your webinar registration page URL"
              className="w-full px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary-500/50 transition-colors"
            />
            <p className="text-xs text-slate-500">
              The URL where prospects will register for your webinar
            </p>
          </div>
        </LaunchCard>

        {/* ── Card 3: Schedule ───────────────────────────────────────────── */}
        <LaunchCard index={2} icon={CalendarDays} title="Schedule">
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Date */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-300">
                  Webinar Date
                  <span className="text-red-400 ml-1">*</span>
                </label>
                <input
                  type="date"
                  value={form.webinar_date}
                  onChange={(e) => updateField('webinar_date', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 focus:outline-none focus:border-primary-500/50 transition-colors [color-scheme:dark]"
                />
              </div>

              {/* Time */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-300">Webinar Time</label>
                <div className="relative">
                  <select
                    value={form.webinar_time}
                    onChange={(e) => updateField('webinar_time', e.target.value)}
                    className="w-full appearance-none px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 focus:outline-none focus:border-primary-500/50 transition-colors pr-10"
                  >
                    <option value="">Select a time</option>
                    {TIME_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                </div>
              </div>
            </div>

            {form.webinar_date && emailTimeline && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-primary-500/5 border border-primary-500/20">
                <CalendarDays className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <p className="text-sm text-primary-300">
                  Your first marketing email goes out <span className="font-semibold text-white">{formatDate(emailTimeline.marketing1)}</span> (10 days before your webinar)
                </p>
              </div>
            )}

            {/* Email Timeline */}
            {emailTimeline ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-3"
              >
                <h3 className="text-sm font-medium text-slate-300">Email Timeline</h3>
                <div className="relative pl-1">
                  {/* Connecting line */}
                  <div className="absolute left-[12px] top-3 bottom-3 w-px bg-gradient-to-b from-primary-500/60 via-slate-600/40 to-purple-500/60" />

                  <TimelineNode
                    date={emailTimeline.marketing1}
                    label="Email 1 — The Mistake Hook"
                    variant="blue"
                  />
                  <TimelineNode
                    date={emailTimeline.marketing2}
                    label="Email 2 — The Origin / Trust"
                    variant="blue"
                  />
                  <TimelineNode
                    date={emailTimeline.marketing3}
                    label="Email 3 — The Logic / Villain"
                    variant="blue"
                  />
                  <TimelineNode
                    date={emailTimeline.marketing4}
                    label="Email 4 — The Goodie Bag"
                    variant="blue"
                  />
                  <TimelineNode
                    date={emailTimeline.marketing5}
                    label="Email 5 — Behind the Scenes"
                    variant="blue"
                  />
                  <TimelineNode
                    date={parseLocalDate(form.webinar_date)}
                    label="Webinar Day"
                    variant="blue"
                    isCenter
                  />
                  <TimelineNode
                    date={emailTimeline.marketing6}
                    label="Email 6 — The Agenda (morning)"
                    variant="blue"
                  />
                  <TimelineNode
                    date={emailTimeline.marketing7}
                    label="Email 7 — Starting in 60 min"
                    variant="blue"
                  />
                  <TimelineNode
                    date={emailTimeline.marketing8}
                    label="Email 8 — WE ARE LIVE"
                    variant="blue"
                  />
                  <TimelineNode
                    date={emailTimeline.followup1}
                    label="Follow-Up 1 — Recap & Shift (4hrs post)"
                    variant="purple"
                  />
                  <TimelineNode
                    date={emailTimeline.followup2}
                    label="Follow-Up 2 — The Case Study"
                    variant="purple"
                  />
                  <TimelineNode
                    date={emailTimeline.followup3}
                    label="Follow-Up 3 — The Spear"
                    variant="purple"
                  />
                </div>
              </motion.div>
            ) : (
              <div className="text-sm text-slate-500 italic py-4 text-center border border-dashed border-slate-700/50 rounded-xl">
                Select a webinar date to see your email schedule
              </div>
            )}
          </div>
        </LaunchCard>

        {/* ── Card: Zoom Meeting ─────────────────────────────────────── */}
        <LaunchCard index={3} icon={Video} title="Zoom Meeting">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-300">
                Zoom Meeting Link
              </label>
              <input
                type="text"
                value={form.zoom_join_link}
                onChange={(e) => updateField('zoom_join_link', e.target.value)}
                placeholder="https://zoom.us/j/123456789"
                className="w-full px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary-500/50 transition-colors"
              />
            </div>
            <p className="text-xs text-slate-500">
              Create your Zoom meeting, then paste the join link here. This link goes in your confirmation and reminder emails.
            </p>
          </div>
        </LaunchCard>

        {/* ── Card: Registrant Pipeline ──────────────────────────────── */}
        <LaunchCard index={4} icon={Activity} title="Registrant Pipeline">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-300">Pipeline</label>
              {pipelinesLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading pipelines...
                </div>
              ) : (
                <div className="relative">
                  <select
                    value={form.pipeline_id}
                    onChange={(e) => {
                      updateField('pipeline_id', e.target.value);
                      updateField('pipeline_stage_id', '');
                    }}
                    className="w-full appearance-none px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 focus:outline-none focus:border-primary-500/50 transition-colors pr-10"
                  >
                    <option value="">Select a pipeline...</option>
                    {pipelines.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                </div>
              )}
            </div>

            {form.pipeline_id && (
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-300">Stage</label>
                <div className="relative">
                  <select
                    value={form.pipeline_stage_id}
                    onChange={(e) => updateField('pipeline_stage_id', e.target.value)}
                    className="w-full appearance-none px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 focus:outline-none focus:border-primary-500/50 transition-colors pr-10"
                  >
                    <option value="">Select a stage...</option>
                    {pipelines
                      .find((p) => p.id === form.pipeline_id)
                      ?.stages.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                </div>
              </div>
            )}

            <p className="text-xs text-slate-500">
              When someone registers for your webinar, they'll appear as an opportunity here so you can track them.
            </p>
          </div>
        </LaunchCard>

        {/* ── Card 6: Your Profile ───────────────────────────────────────── */}
        <LaunchCard
          index={5}
          icon={User}
          title="Your Profile"
          warningBorder={!profileLoading && !profileComplete}
        >
          {profileLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 text-slate-500 animate-spin" />
            </div>
          ) : profileComplete && !showProfileForm ? (
            <div className="flex items-center gap-4">
              {profile!.advisor_headshot ? (
                <ImagePreview url={profile!.advisor_headshot} size={64} />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <User className="w-7 h-7 text-slate-500" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{profile!.advisor_name}</p>
                {profile!.company_name && (
                  <p className="text-sm text-slate-400 truncate">{profile!.company_name}</p>
                )}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                  <Check className="w-3 h-3" />
                  Complete
                </span>
                <button
                  onClick={() =>
                    navigate('/settings')
                  }
                  className="inline-flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300 transition-colors"
                >
                  Edit in Settings
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {!profileComplete && (
                <div className="flex items-center gap-2 text-amber-400 text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>Complete your profile before launching</span>
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-300">Name *</label>
                  <input
                    type="text"
                    value={profileForm.advisor_name}
                    onChange={(e) =>
                      setProfileForm((p) => ({ ...p, advisor_name: e.target.value }))
                    }
                    placeholder="Your name"
                    className="w-full px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-300">Company</label>
                  <input
                    type="text"
                    value={profileForm.company_name}
                    onChange={(e) =>
                      setProfileForm((p) => ({ ...p, company_name: e.target.value }))
                    }
                    placeholder="Company name"
                    className="w-full px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-300">Bio</label>
                  <textarea
                    value={profileForm.bio}
                    onChange={(e) => setProfileForm((p) => ({ ...p, bio: e.target.value }))}
                    rows={3}
                    placeholder="A brief bio..."
                    className="w-full px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 placeholder:text-slate-500 resize-y focus:outline-none focus:border-primary-500/50 transition-colors"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-300">Headshot URL</label>
                  <div className="flex items-start gap-3">
                    <input
                      type="text"
                      value={profileForm.advisor_headshot}
                      onChange={(e) =>
                        setProfileForm((p) => ({ ...p, advisor_headshot: e.target.value }))
                      }
                      placeholder="https://example.com/headshot.jpg"
                      className="flex-1 px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                    />
                    <ImagePreview url={profileForm.advisor_headshot} size={48} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-300">Calendar Link</label>
                  <input
                    type="text"
                    value={profileForm.cta_link}
                    onChange={(e) =>
                      setProfileForm((p) => ({ ...p, cta_link: e.target.value }))
                    }
                    placeholder="Your Calendly or booking page URL"
                    className="w-full px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                  />
                </div>
              </div>

              <button
                onClick={handleProfileSave}
                disabled={profileSaving || !profileForm.advisor_name.trim()}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-500 text-white font-medium rounded-xl text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {profileSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Profile
              </button>
            </div>
          )}
        </LaunchCard>

        {/* ── Card 7: Launch Readiness ───────────────────────────────────── */}
        <LaunchCard index={6} icon={ClipboardCheck} title="Launch Readiness">
          <div className="space-y-5">
            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">
                  {checklist.doneCount} of {checklist.total} items complete
                </span>
                <span className="text-primary-400 font-medium">
                  {Math.round((checklist.doneCount / checklist.total) * 100)}%
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-primary-500 to-purple-500"
                  initial={{ width: 0 }}
                  animate={{
                    width: `${(checklist.doneCount / checklist.total) * 100}%`,
                  }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                />
              </div>
            </div>

            {/* Checklist items */}
            <div className="space-y-2.5">
              {checklist.items.map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  {item.done ? (
                    <div className="w-5 h-5 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-green-400" />
                    </div>
                  ) : (
                    <Circle className="w-5 h-5 text-slate-600 flex-shrink-0" />
                  )}
                  <span
                    className={`text-sm ${
                      item.done ? 'text-slate-400' : 'text-white font-medium'
                    }`}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </LaunchCard>

        {/* ── Bottom Action Bar ──────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="flex items-center justify-between pt-4 pb-8"
        >
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                navigate(webinarId ? `/results/${webinarId}` : '/');
              }}
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Results
            </button>
            {savedForm && webinarId && (
              <button
                onClick={() => navigate(`/status/${webinarId}`)}
                className="inline-flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 transition-colors"
              >
                <Activity className="w-4 h-4" />
                View Status
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Save Draft */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium rounded-xl border border-slate-700 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Draft
            </motion.button>

            {/* Confirm & Launch (disabled for now) */}
            <div className="relative group/launch">
              <motion.button
                disabled
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white font-medium rounded-xl opacity-50 cursor-not-allowed shadow-lg shadow-primary-500/10"
              >
                <Lock className="w-4 h-4" />
                Confirm & Launch
              </motion.button>

              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-300 whitespace-nowrap opacity-0 group-hover/launch:opacity-100 transition-opacity pointer-events-none">
                GHL deployment coming soon
                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-slate-800" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* ── Toast ──────────────────────────────────────────────────────── */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: 50, x: '-50%' }}
          animate={{ opacity: 1, y: 0, x: '-50%' }}
          exit={{ opacity: 0, y: 50 }}
          className={`fixed bottom-6 left-1/2 px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg z-50 ${
            toast.type === 'success'
              ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-300'
              : 'bg-red-500/20 border border-red-500/30 text-red-300'
          }`}
        >
          {toast.type === 'success' ? (
            <Check className="w-4 h-4" />
          ) : (
            <AlertTriangle className="w-4 h-4" />
          )}
          {toast.message}
        </motion.div>
      )}
    </div>
  );
}
